/* MIH — Pincode → Estimated Delivery widget
 *
 * Two modes:
 *   1. Live mode — calls a Cloudflare Worker proxy that talks to Shiprocket's
 *      serviceability API. Returns real courier name + actual ETD.
 *   2. Fallback mode — zone-based estimate using first digit of pincode.
 *      Used when the API URL isn't configured or the Worker call fails.
 *
 * Worker URL is supplied via the [data-api-url] attribute (set in Liquid from
 * the section settings).
 */
(function () {
  'use strict';

  function bucketFor(buyerPin, pickupPin) {
    if (!/^\d{6}$/.test(buyerPin)) return null;
    var b = buyerPin.charAt(0);
    var p = pickupPin && pickupPin.charAt(0);
    if (pickupPin && buyerPin.slice(0, 2) === pickupPin.slice(0, 2)) return 1;
    if (p === '3') {
      switch (b) {
        case '1': return 2;
        case '2': return 2;
        case '3': return 1;
        case '4': return 3;
        case '5': return 4;
        case '6': return 4;
        case '7': return 4;
        case '8': return 5;
        case '9': return 5;
      }
    }
    if (!p) return 3;
    var diff = Math.abs(parseInt(b, 10) - parseInt(p, 10));
    if (diff === 0) return 1;
    if (diff === 1) return 2;
    if (diff === 2) return 3;
    if (diff === 3) return 4;
    return 5;
  }

  function formatDate(d) {
    var months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    var days = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
    return days[d.getDay()] + ', ' + months[d.getMonth()] + ' ' + d.getDate();
  }

  function addBusinessDays(start, days) {
    var d = new Date(start.getTime());
    var added = 0;
    while (added < days) {
      d.setDate(d.getDate() + 1);
      if (d.getDay() !== 0) added++;
    }
    return d;
  }

  function parseEtd(etdStr) {
    // Shiprocket etd can be "Jun 10, 2026" or "2026-06-10". Try both.
    if (!etdStr) return null;
    var d = new Date(etdStr);
    return isNaN(d.getTime()) ? null : d;
  }

  function renderResult(result, opts) {
    var rows = [
      {
        ok: true,
        html:
          'Delivered by <strong>' + result.etaText + '</strong> &middot; ' +
          result.days + ' business day' + (result.days === 1 ? '' : 's') +
          (result.courier ? ' &middot; via ' + escapeHtml(result.courier) : ''),
      },
      {
        ok: result.codAvailable !== false,
        html: result.codAvailable !== false
          ? 'Cash on Delivery available'
          : 'Cash on Delivery <em>not available</em> for this pincode',
      },
    ];
    if (opts.showFreeShip && opts.freeShipThreshold > 0) {
      rows.push({
        ok: true,
        html: 'Free shipping on orders above &#8377;' + opts.freeShipThreshold,
      });
    }
    var html = '';
    rows.forEach(function (r) {
      var icon = r.ok
        ? '<span class="mih-pin__check" aria-hidden="true">&#10003;</span>'
        : '<span class="mih-pin__check mih-pin__check--no" aria-hidden="true">&#10005;</span>';
      html += '<div class="mih-pin__result-row">' + icon + '<span>' + r.html + '</span></div>';
    });
    return html;
  }

  function escapeHtml(s) {
    return String(s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function fallbackEta(pin, pickup, dayMap) {
    var bucket = bucketFor(pin, pickup);
    if (!bucket) return null;
    var days = dayMap[bucket];
    var eta = addBusinessDays(new Date(), days);
    return {
      source: 'fallback',
      days: days,
      etaText: formatDate(eta),
      courier: null,
      codAvailable: true, // optimistic for zone fallback
    };
  }

  async function liveEta(apiUrl, pin, pickup, weight) {
    var url = apiUrl.replace(/\/+$/, '') +
      '/?pin=' + encodeURIComponent(pin) +
      '&from=' + encodeURIComponent(pickup) +
      '&weight=' + encodeURIComponent(weight || 1);
    var res = await fetch(url, { method: 'GET', mode: 'cors', credentials: 'omit' });
    if (!res.ok) throw new Error('http_' + res.status);
    var data = await res.json();
    if (data.error) throw new Error(data.error);
    if (!data.days) throw new Error('no_days');
    var etaDate = parseEtd(data.etd) || addBusinessDays(new Date(), data.days);
    return {
      source: 'live',
      days: data.days,
      etaText: formatDate(etaDate),
      courier: data.courier,
      codAvailable: !!data.cod,
    };
  }

  function init(root) {
    var form = root.querySelector('[data-mih-pin-form]');
    var input = root.querySelector('[data-mih-pin-input]');
    var result = root.querySelector('[data-mih-pin-result]');
    var btn = root.querySelector('[data-mih-pin-btn]');
    if (!form || !input || !result || !btn) return;

    var apiUrl = root.dataset.apiUrl || '';
    var pickup = root.dataset.pickup || '302020';
    var dayMap = {
      1: parseInt(root.dataset.zone1, 10) || 1,
      2: parseInt(root.dataset.zone2, 10) || 3,
      3: parseInt(root.dataset.zone3, 10) || 4,
      4: parseInt(root.dataset.zone4, 10) || 5,
      5: parseInt(root.dataset.zone5, 10) || 7,
    };
    var showFreeShip = root.dataset.showFreeShip === 'true';
    var freeShipThreshold = parseInt(root.dataset.freeShip, 10) || 0;

    input.addEventListener('input', function () {
      input.value = input.value.replace(/\D/g, '').slice(0, 6);
      input.classList.remove('is-error');
    });

    form.addEventListener('submit', async function (e) {
      e.preventDefault();
      var pin = (input.value || '').replace(/\D/g, '');
      if (pin.length !== 6) {
        input.classList.add('is-error');
        result.hidden = false;
        result.className = 'mih-pin__result mih-pin__result--err';
        result.textContent = 'Please enter a valid 6-digit pincode.';
        return;
      }

      btn.disabled = true;
      var originalLabel = btn.textContent;
      btn.textContent = 'Checking…';

      var data = null;
      var invalidPincode = false;
      // Try live API first, fall back to zone-based on transport errors only.
      if (apiUrl) {
        try {
          data = await liveEta(apiUrl, pin, pickup, 1);
        } catch (err) {
          // Distinguish "this pincode does not exist" from network/upstream issues.
          // Worker returns 'invalid_pincode' when India Post has no record for it.
          if (String(err && err.message) === 'invalid_pincode') {
            invalidPincode = true;
          }
          data = null;
        }
      }
      if (!data && !invalidPincode) data = fallbackEta(pin, pickup, dayMap);

      btn.disabled = false;
      btn.textContent = originalLabel;

      if (invalidPincode) {
        input.classList.add('is-error');
        result.hidden = false;
        result.className = 'mih-pin__result mih-pin__result--err';
        result.textContent = 'Enter a valid pincode.';
        return;
      }

      if (!data) {
        result.hidden = false;
        result.className = 'mih-pin__result mih-pin__result--err';
        result.textContent = 'Sorry, we could not estimate delivery for this pincode.';
        return;
      }

      result.hidden = false;
      result.className = 'mih-pin__result mih-pin__result--ok';
      result.innerHTML = renderResult(data, {
        showFreeShip: showFreeShip,
        freeShipThreshold: freeShipThreshold,
      });
    });
  }

  function boot() {
    document.querySelectorAll('[data-mih-pincode-eta]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
